import { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FooterSG } from '../FooterSG';
import { Ball75 } from '../Ball75';
import { Ball80 } from '../Ball80';
import { Ball90 } from '../Ball90';
import { Ball30 } from '../Ball30';
import { ToGoPanel } from '../ToGoPanel';
import { GamePart, GamePartProps, GamePartDot } from '../GamePart';
import {
    CallsCard,
    CallsCardProps,
    CallsText,
    CallsNumber,
} from '../CallsCard';
import { MessageBubble } from '../MessageBubble';

type PlayModeSGProps = CallsCardProps &
    GamePartProps & {
        calledBalls: number[];
        togos: number[];
        mentions: number;
        showMessageBubble: boolean;
        handleMentions: () => void;
    };

const PlayModeSGContainer = styled(FooterSG)`
    align-items: center;
`;

const CallsCardWrapper = styled(CallsCard)`
    width: 5rem;
    height: 5rem;
    padding: 0.438rem 0.75rem 0.125rem;
    margin-right: 0.375rem;
    border-width: 0.25rem;

    ${CallsText} {
        font-size: 1.25rem;
        line-height: 1.25rem;
    }

    ${CallsNumber} {
        font-size: 2.125rem;
        line-height: 2.125rem;
    }
`;

const FlexRowWrapper = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-basis: 55%;
`;

const FlexColWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
    flex-basis: 40%;
    margin-left: 0.375rem;
`;

const GamePartWrapper = styled(GamePart)`
    margin-bottom: 0.625rem;
    ${GamePartDot} {
        width: 0.938rem;
        height: 0.938rem;
    }
`;

const CalledBallsWrapper = styled.div`
    display: flex;
    flex: 1;
    position: relative;
    height: 5rem;
    margin-top: 0.625rem;
    overflow: hidden;
`;

const BallTrayItem = styled.div<{ x: number }>`
    user-select: none;
    position: absolute;
    transition: 0.5s ease-in-out left, 0.5s ease-in-out transform;
    transform: ${(props) => `rotate(${props.x * 360}deg)`};
    left: ${(props) => props.x * 4.688 + 'rem'};
`;

const TogoPanelContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ToGoPanelWrapper = styled(ToGoPanel)`
    width: 4.5rem;
    height: 2.5rem;
    font-size: 1rem;
    border: none;
`;

const BALLS = Array.from({ length: 90 }).map((_v, i) => i + 1);
const BALL_COMPONENTS = {
    75: Ball75,
    80: Ball80,
    90: Ball90,
    30: Ball30,
};

export const PlayModeSGPortrait: FC<PlayModeSGProps> = ({
    calls,
    calledBalls,
    togos,
    gameType,
    gameParts,
    currentPart,
    fireBall,
    mentions,
    showMessageBubble,
    handleMentions,
    ...props
}) => {
    const { t } = useTranslation();
    // slice first before reverse to make a new copy of the array
    const $recentBalls = calledBalls.slice().reverse();
    const Ball = BALL_COMPONENTS[gameType];

    return (
        <PlayModeSGContainer showFooter={true} {...props}>
            <FlexRowWrapper>
                <CallsCardWrapper calls={calls} gameType={gameType} />
                <CalledBallsWrapper>
                    {BALLS.map((ball) => {
                        return (
                            <BallTrayItem
                                as={Ball}
                                size="xsmall"
                                key={ball}
                                number={ball}
                                x={$recentBalls.indexOf(ball)}
                            />
                        );
                    })}
                </CalledBallsWrapper>
            </FlexRowWrapper>
            <FlexColWrapper>
                <GamePartWrapper
                    gameParts={gameParts}
                    currentPart={currentPart}
                    fireBall={fireBall}
                />
                <TogoPanelContainer>
                    {togos.map((togo, index) => {
                        const LABEL = {
                            1: t('cardDeck.OneToGo'),
                            2: t('cardDeck.TwoToGo'),
                            3: t('cardDeck.ThreeToGo'),
                            4: t('cardDeck.FourOrMoreToGo'),
                        };

                        return (
                            <ToGoPanelWrapper
                                key={index}
                                label={LABEL[(index + 1) as keyof typeof LABEL]}
                                value={togo}
                            />
                        );
                    })}
                </TogoPanelContainer>
            </FlexColWrapper>
            {showMessageBubble && (
                <MessageBubble
                    mentions={mentions}
                    handleClick={handleMentions}
                />
            )}
        </PlayModeSGContainer>
    );
};
